import { useState } from "react";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";

import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Select,
  Text,
  Textarea,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { CloseIcon, ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";

import { format } from "date-fns";
import { formatAUTime24 } from "../global-client";
import { useDispatch } from "react-redux";
import { addJobLogEntry, updateJobLogEntry } from "../redux/actions/jobActions";
import { compressImages, calculateCompressionRatio } from "../utils/imageCompression";

/**
 * JobLogForm.jsx
 *
 * Usage:
 * <JobLogForm
 *   initialValues={...} // optional
 *   existingLog={...} // optional, for editing
 *   isModal={true} // optional, removes border and padding for modal usage
 *   onSuccess={(created) => {...}} // optional callback after successful submit
 * />
 */

const validationSchema = Yup.object({
  //jobId: Yup.string().trim().required("Job ID is required"),
  //date: Yup.date().required("Date is required").max(new Date(), "Date cannot be in the future"),
  date: Yup.date().required("Date is required"),
  time: Yup.string().required("Time is required"),
  //hours: Yup.number().required("Hours worked is required").min(0, "Hours must be >= 0").max(24, "Hours must be <= 24"),
  title: Yup.string().trim().required("Title is required").min(5),
  description: Yup.string(),
  status: Yup.string()
    .oneOf(["Comment", "Hidden", "Started", "Paused", "Finished"], "Invalid status")
    .required("Status is required"),
  photos: Yup.array()
    .of(
      Yup.object({
        name: Yup.string().nullable(),
        size: Yup.number().nullable(),
        type: Yup.string().nullable(),
        data: Yup.string().required("Image data is required"),
      })
    )
    .notRequired(),
  //billable: Yup.boolean(),
  //cost: Yup.number().min(0, "Cost must be >= 0"),
});

const getDefaultValues = () => ({
  jobId: "",
  date: format(new Date(), "yyyy-MM-dd"), // YYYY-MM-DD
  time: formatAUTime24(new Date()), // HH:MM in 24-hour format
  hours: 0,
  title: "",
  description: "",
  status: "Comment",
  billable: false,
  cost: 0,
  photos: [],
});

export default function JobLogForm({
  initialValues = {},
  job,
  existingLog,
  onSuccess,
  isModal = false,
} = {}) {
  const toast = useToast();
  const dispatch = useDispatch();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [previewIndex, setPreviewIndex] = useState(-1);

  const defaultValues = getDefaultValues();
  
  const formInitialValues = existingLog
    ? {
        ...defaultValues,
        ...existingLog,
        date: existingLog.date ? format(new Date(existingLog.date), "yyyy-MM-dd") : defaultValues.date,
        time: existingLog.date ? formatAUTime24(new Date(existingLog.date)) : defaultValues.time,
      }
    : { ...defaultValues, ...initialValues };

  const convertFilesToPhotos = async (files) => {
    try {
      const compressed = await compressImages(files);
      
      // Show compression stats in console for debugging
      const stats = compressed.map((photo) => ({
        name: photo.name,
        original: `${(photo.originalSize / 1024).toFixed(2)}KB`,
        compressed: `${(photo.size / 1024).toFixed(2)}KB`,
        reduction: `${(calculateCompressionRatio(photo.originalSize, photo.size) * 100).toFixed(1)}%`,
      }));
      console.log("Image compression stats:", stats);
      
      return compressed;
    } catch (error) {
      console.error("Error compressing images:", error);
      toast({
        title: "Warning",
        description: "Could not compress images optimally. Using original files.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      // Fallback to original compression if smart compression fails
      return Promise.all(
        Array.from(files || []).map(
          (file) =>
            new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                resolve({
                  name: file.name,
                  size: file.size,
                  type: file.type,
                  data: reader.result,
                });
              };
              reader.readAsDataURL(file);
            })
        )
      );
    }
  };

  const handlePreview = (index) => {
    setPreviewIndex(index);
    onOpen();
  };

  const handleNextImage = () => {
    setPreviewIndex((prev) => (prev < (formInitialValues.photos?.length || 0) - 1 ? prev + 1 : 0));
  };

  const handlePrevImage = () => {
    setPreviewIndex((prev) => (prev > 0 ? prev - 1 : (formInitialValues.photos?.length || 1) - 1));
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const dateTime = new Date(`${values.date}T${values.time}`);
      const adjustedValues = {
        ...values,
        date: dateTime.toISOString(),
      };
      delete adjustedValues.time; // remove time field as it's now part of date
      const completeValues = { ...adjustedValues, jobId: job.JOB_ID, jobNo: job.JOB_NO };
      console.log("Submitting job log:", completeValues);

      if (existingLog) {
        dispatch(updateJobLogEntry(existingLog._id, completeValues));
        toast({
          title: "Job log updated",
          status: "success",
          duration: 3000,
        });
      } else {
        dispatch(addJobLogEntry(completeValues));
        toast({
          title: "Job log saved",
          status: "success",
          duration: 3000,
        });
        resetForm();
      }

      if (onSuccess) onSuccess(false); //close the form on success
    } catch (err) {
      console.error(err);
      toast({
        title: "Error saving job log",
        description: err?.response?.data?.message || err.message,
        status: "error",
        duration: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      maxW={isModal ? "100%" : "720px"}
      w='100%'
      p={isModal ? 0 : 4}
      borderWidth={isModal ? 0 : 1}
      borderRadius={isModal ? 0 : "md"}
    >
      <Formik initialValues={formInitialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {({ isSubmitting, values, setFieldValue }) => (
          <>
            <Form>
              <VStack spacing={4} align='stretch'>
                <HStack spacing={4}>
                  <Field name='date'>
                    {({ field, form }) => (
                      <FormControl isInvalid={form.errors.date && form.touched.date}>
                        <FormLabel>Date</FormLabel>
                        <Input {...field} type='date' />
                        <FormErrorMessage>{form.errors.date}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name='time'>
                    {({ field, form }) => (
                      <FormControl isInvalid={form.errors.time && form.touched.time}>
                        <FormLabel>Time</FormLabel>
                        <Input {...field} type='time' />
                        <FormErrorMessage>{form.errors.time}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </HStack>

                <Field name='title'>
                  {({ field, form }) => (
                    <FormControl isInvalid={form.errors.title && form.touched.title}>
                      <FormLabel>Work performed</FormLabel>
                      <Input {...field} placeholder='Describe the work performed' />
                      <FormErrorMessage>{form.errors.title}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
                <Field name='description'>
                  {({ field, form }) => (
                    <FormControl isInvalid={form.errors.description && form.touched.description}>
                      <FormLabel>Description</FormLabel>
                      <Textarea {...field} placeholder='More detailed description' />
                      <FormErrorMessage>{form.errors.description}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>

                <Field name='photos'>
                  {({ field, form }) => (
                    <FormControl isInvalid={form.errors.photos && form.touched.photos}>
                      <FormLabel>Images</FormLabel>
                      <Input
                        pt='1'
                        type='file'
                        accept='image/*'
                        multiple
                        onChange={async (e) => {
                          const files = e.target.files;
                          if (!files?.length) {
                            return;
                          }
                          const converted = await convertFilesToPhotos(files);
                          const updated = [...(values.photos || []), ...converted];
                          setFieldValue("photos", updated);
                          form.setFieldTouched("photos", true, true);
                        }}
                      />
                      {values.photos && values.photos.length > 0 && (
                        <VStack align='stretch' spacing={2} mt={3}>
                          {values.photos.map((photo, index) => (
                            <Box
                              key={`${photo.name || "photo"}-${index}`}
                              p={2}
                              border='1px solid'
                              borderColor='gray.200'
                              borderRadius='md'
                              display='flex'
                              alignItems='center'
                              gap={3}
                            >
                              {photo.data && (
                                <Image
                                  src={photo.data}
                                  alt={photo.name || `Image ${index + 1}`}
                                  boxSize='60px'
                                  objectFit='cover'
                                  borderRadius='md'
                                  cursor='pointer'
                                  _hover={{ opacity: 0.8 }}
                                  onClick={() => handlePreview(index)}
                                />
                              )}
                              <Box flex='1'>
                                <Text fontSize='sm' fontWeight='500'>
                                  {photo.name || `Photo ${index + 1}`}
                                </Text>
                                <Text fontSize='xs' color='gray.500'>
                                  {photo.size ? `${(photo.size / 1024).toFixed(2)} KB` : photo.type || "Image"}
                                </Text>
                              </Box>
                              <IconButton
                                aria-label='Delete photo'
                                icon={<CloseIcon />}
                                size='sm'
                                colorScheme='red'
                                variant='ghost'
                                onClick={() => {
                                  const updatedPhotos = values.photos.filter((_, i) => i !== index);
                                  setFieldValue("photos", updatedPhotos);
                                }}
                              />
                            </Box>
                          ))}
                        </VStack>
                      )}
                      <FormErrorMessage>{form.errors.photos}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>

                <HStack spacing={4}>
                  <Field name='status'>
                    {({ field, form }) => (
                      <FormControl isInvalid={form.errors.status && form.touched.status}>
                        <FormLabel>Status</FormLabel>
                        <Select {...field}>
                          <option value='Comment'>Comment</option>
                          <option value='Hidden'>Hidden Comment</option>
                          <option value='Started'>Started</option>
                          <option value='Paused'>Paused</option>
                          <option value='Finished'>Finished</option>
                        </Select>
                        <FormErrorMessage>{form.errors.status}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </HStack>

                <HStack justify='flex-end' mt={2}>
                  <Button
                    colorScheme='gray'
                    variant='outline'
                    onClick={() => {
                      // reset to initial values
                      if (typeof initialValues === "function") return;
                      // Using Formik resetForm would be better, but we don't have access to helpers here.
                      // Triggering page reload fallback:
                      window?.location?.reload?.();
                    }}
                  >
                    Reset
                  </Button>
                  <Button colorScheme='blue' type='submit' isLoading={isSubmitting}>
                    Save Job Log
                  </Button>
                </HStack>
              </VStack>
            </Form>
            <Modal isOpen={isOpen} onClose={onClose} size='6xl' isCentered>
              <ModalOverlay bg='blackAlpha.800' />
              <ModalContent bg='transparent' boxShadow='none'>
                <ModalCloseButton color='white' size='lg' />
                <ModalBody p={0} display='flex' justifyContent='center' alignItems='center' bg='black' minH='100vh'>
                  {previewIndex >= 0 && values.photos?.[previewIndex] && (
                    <HStack spacing={4} w='100%' justify='center' align='center'>
                      {values.photos?.length > 1 && (
                        <IconButton
                          aria-label='Previous image'
                          icon={<ChevronLeftIcon w={10} h={10} />}
                          color='white'
                          bg='rgba(0, 0, 0, 0.8)'
                          _hover={{ bg: "rgba(0, 0, 0, 0.95)" }}
                          onClick={handlePrevImage}
                          size='2xl'
                          isRound
                        />
                      )}
                      <Image
                        src={values.photos[previewIndex].data}
                        alt={values.photos[previewIndex].name || `Image ${previewIndex + 1}`}
                        maxH='90vh'
                        maxW='70vw'
                        objectFit='contain'
                        borderRadius='md'
                      />
                      {values.photos?.length > 1 && (
                        <IconButton
                          aria-label='Next image'
                          icon={<ChevronRightIcon w={10} h={10} />}
                          color='white'
                          bg='rgba(0, 0, 0, 0.8)'
                          _hover={{ bg: "rgba(0, 0, 0, 0.95)" }}
                          onClick={handleNextImage}
                          size='2xl'
                          isRound
                        />
                      )}
                    </HStack>
                  )}
                </ModalBody>
              </ModalContent>
            </Modal>
          </>
        )}
      </Formik>
    </Box>
  );
}
